<template>
  <div class="relative space-y-4" :class="{ 'min-h-[16rem]': showOverlay }">
    <div
      v-if="error || paymentError"
      class="p-3 bg-danger/10 text-danger-fg rounded-lg"
      role="alert"
    >
      {{ error || paymentError }}
    </div>
    <!-- Single unified loader. Covers the two otherwise-blank windows QA
         flagged as a "white area + faint loader":
           1. chargebee.js script-load + iframe mount on first render, and
           2. the gRPC submit window after chargebee authorizes the intent
              (`submitted`), before the host navigates away / closes.
         Rendered as an absolute overlay so the real chargebee mounts below
         keep their layout box — chargebee sizes its iframe from the
         container, so display:none here would collapse it to the 150px
         default. The surface color matches the host so the overlay blends
         in instead of flashing a mismatched panel. -->
    <div
      v-if="showOverlay"
      class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-lg"
      :style="{ backgroundColor: surfaceColor ?? GP_DEPTH_1 }"
    >
      <div
        class="size-10 rounded-full border-4 border-depth-3 border-t-brand animate-spin"
      ></div>
      <p class="text-rhino text-sm">
        {{
          submitted
            ? t('payment/form/saving')
            : t('general/non_special_texts/loading')
        }}
      </p>
    </div>
    <!-- chargebee.components('payment') renders the form fields and method
         picker here. The chargebee iframe is styled `height: 100%`, which
         only resolves when its parent has an explicit pixel/vh height.
         Without min-h, the iframe collapses to its 150px HTML default and
         the form looks clipped. 640px covers the accordion rows + card
         inputs for the common methods; chargebee.js' built-in resize
         logic grows the iframe past this when content needs more. -->
    <div ref="paymentMount" class="chargebee-payment-mount h-auto" />
    <!-- Host content rendered between the form fields and the pay button — used
         by the checkout step to place the mandatory T&C/Privacy consent
         checkbox directly above the button (below the form). Spaced by the
         root's space-y-4. -->
    <slot v-if="!hideSubmit" name="before-button" />
    <!-- chargebee.components('payment-button') is a separate component
         that renders the submit/pay button. It internally talks to the
         payment component above to tokenize and submit the intent. The
         payment component doesn't have its own submit button.

         Once chargebee authorizes the intent we set `submitted` and
         display:none this wrapper, so the iframe-rendered button is
         no longer clickable while the host's gRPC SubmitCheckout /
         SubmitPaymentMethodSetup completes. The composable also calls
         payment-button .close() in the same tick to tear down the
         chargebee component itself — the CSS hide is the immediate
         guarantee, the close() is the chargebee-side cleanup. -->
    <!-- v-show, not v-if: the button iframe stays mounted while the host shows
         its own button for a saved method, so switching back does not reload it. -->
    <div v-show="!hideSubmit" class="relative">
      <!-- `inert` when disabled removes the chargebee button (and its iframe)
           from tab order + pointer/click handling, so keyboard activation can't
           reach chargebee and trip the onButtonClick reject — the overlay below
           only intercepts mouse clicks. `|| null` so the attribute is absent
           (not inert="false") once consent is given. -->
      <div
        ref="buttonMount"
        class="chargebee-payment-button-mount"
        :class="{ hidden: submitted }"
        :inert="(submitDisabled && !submitted) || null"
      />
      <!-- Consent gate. When submitDisabled, this transparent layer sits over
           the chargebee pay button (which lives in an iframe we can't restyle
           from here) to capture the mouse click before chargebee
           tokenizes/authorizes the intent, dim the button as a disabled cue,
           and emit `blocked` so the host can point the user at the unmet
           requirement. Keyboard activation is handled by `inert` above;
           onButtonClick is the authoritative backstop; this is the UX layer. -->
      <div
        v-if="submitDisabled && !submitted"
        class="absolute inset-0 z-20 cursor-not-allowed rounded-lg bg-depth-1/50"
        role="button"
        tabindex="-1"
        :aria-label="disabledHint"
        @click="emit('blocked')"
      />
    </div>
    <!-- Redirect methods (PayPal, Klarna) and 3-D Secure
         open a window chargebee manages; a buyer who closes it is left
         wondering whether they paid. Shown once the form is usable and until
         it is submitted. -->
    <p
      v-if="cancelHint && !hideSubmit && !loading && !submitted && !error"
      class="flex items-start gap-2 text-sm text-rhino"
      data-test="chargebee-cancel-hint"
    >
      <fa-icon
        :icon="faCircleInfo"
        class="mt-0.5 size-4 shrink-0"
        aria-hidden="true"
      />
      <span>{{ cancelHint }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/nuxt';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { GP_DEPTH_1 } from '~/composables/useChargebeePayment';

const { t } = useI18n();

const props = defineProps<{
  /**
   * payment_intent JSON returned by CreateCheckoutPaymentIntent /
   * CreateSetupPaymentIntent. Forwarded verbatim to chargebee.components.
   */
  paymentIntent: Record<string, unknown>;
  /**
   * Submit handler — caller invokes their RPC with payment_intent.id.
   * Throw to bubble the failure up to onError.
   */
  onSubmit: (paymentIntentId: string) => Promise<void>;
  /**
   * Optional submit-button label override. Default chargebee text is
   * "Pay & Subscribe", which is right for the subscription checkout
   * flow but wrong for the "Add payment method" setup-intent flow.
   * Pass an explicit string from the caller (e.g. an i18n message) to
   * replace the default for every payment method tab.
   */
  submitButtonLabel?: string;
  /**
   * Optional surface color for the chargebee form canvas. Defaults to
   * GP_DEPTH_1 (the checkout step's container background). The support
   * modal sits on depth-2 and passes GP_DEPTH_2 so the chargebee canvas
   * doesn't look like a mismatched paste-in.
   */
  surfaceColor?: string;
  /**
   * Suppress this component's own loader overlay during the chargebee.js
   * mount window, leaving the host to show a single loader instead. The
   * host listens for `ready` to know when the form is interactive. The
   * saving overlay (post-submit) is unaffected. Used by the support modal
   * so its loader doesn't hand off to a second spinner here.
   */
  suppressMountLoader?: boolean;
  /**
   * Pre-submit gate forwarded to chargebee's payment component (see
   * useChargebeePayment MountPaymentFormArgs.onButtonClick). Rejecting blocks
   * authorization. The checkout step passes a gate that rejects until the
   * mandatory T&C/Privacy checkbox is ticked.
   */
  onButtonClick?: () => Promise<void>;
  /**
   * When true, overlays the chargebee pay button with a click-capturing,
   * dimming layer so the intent can't be authorized yet. Paired with
   * onButtonClick for the checkout T&C gate; a blocked click emits `blocked`.
   */
  submitDisabled?: boolean;
  /**
   * Accessible label / hint for the disabled-button overlay (e.g. "please
   * accept the terms first"). Only shown to assistive tech; the visible
   * message is rendered by the host next to the checkbox.
   */
  disabledHint?: string;
  /**
   * The methods to offer, in display order (see useChargebeePayment
   * MountPaymentFormArgs.paymentMethods). Defaults to the checkout's.
   */
  paymentMethods?: string[];
  /**
   * The subscription the payment starts, which Klarna needs for its recurring
   * mandate (see useChargebeePayment MountPaymentFormArgs.subscription);
   * other methods ignore it.
   */
  subscription?: {
    /** Chargebee billing period unit of the plan: day / week / month / year. */
    periodUnit: 'day' | 'week' | 'month' | 'year';
    /** Number of periodUnits between renewals. */
    period: number;
  };
  /**
   * The customer's billing details, prefilled into the chargebee payment
   * component's `context.billingAddress` (see useChargebeePayment
   * MountPaymentFormArgs). Only non-empty fields are forwarded. Sourced from
   * CreateCheckoutPaymentIntent.billing_address.
   */
  billingAddress?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    zip?: string;
    countryCode?: string;
  };
  /**
   * Note shown under the pay button that closing the payment window charges
   * nothing. Omitted, no note is shown.
   */
  cancelHint?: string;
  /**
   * Whether chargebee opens a method row by itself on mount. See
   * useChargebeePayment MountPaymentFormArgs.selectDefaultPaymentMethod.
   */
  selectDefaultPaymentMethod?: boolean;
  /**
   * Hides the pay button, the content above it and the cancel note — the
   * host's own button pays while one of its rows is selected. The form's
   * method rows stay visible and selectable.
   */
  hideSubmit?: boolean;
}>();

const emit = defineEmits<{
  success: [];
  error: [unknown];
  close: [];
  ready: [];
  blocked: [];
  'method-change': [string];
}>();

const paymentMount = ref<HTMLDivElement | null>(null);
const buttonMount = ref<HTMLDivElement | null>(null);
const error = ref('');
// A recoverable payment failure, shown until the customer tries again or picks
// another method. Unlike `error` it leaves the form usable.
const paymentError = ref('');
// True until mountPaymentForm resolves — chargebee.js' script load + iframe
// mount is the "blank white area" QA saw on open. Drives the loader overlay.
const loading = ref(true);
// Flipped via the composable's onSubmitted callback the moment
// chargebee acknowledges the user's submission. Hides the button mount
// before the host's gRPC submit even starts, so the user can't
// re-click during the network round-trip.
const submitted = ref(false);

// Show the loader overlay while bootstrapping the form or saving the
// payment, but never on top of an error (the error banner owns that state).
const showOverlay = computed(
  () =>
    !error.value &&
    ((loading.value && !props.suppressMountLoader) || submitted.value),
);

const { mountPaymentForm } = useChargebeePayment();

let cleanup: (() => void) | null = null;
let isUnmounted = false;

// After a refused payment Chargebee's own pay button can stay in its
// "Processing payment…" state until a reload (QA, dev-alex, twice in two
// tries). It lives in Chargebee's iframe and cannot be reset from here, so the
// form is mounted afresh: a new button, the rows closed, our message above.
// teardown suppresses onClose, so this is not taken for a cancel.
//
// Only for an attempt the customer started: Chargebee also reports errors
// while the form renders, and remounting for one of those mounts the same
// failure again, round after round. payAttempted is set by the pay button and
// cleared here, so each remount needs a new click.
let payAttempted = false;

function remountAfterPaymentError(): void {
  if (isUnmounted || !payAttempted) return;
  payAttempted = false;
  cleanup?.();
  cleanup = null;
  submitted.value = false;
  loading.value = true;
  void nextTick(mountForm);
}

async function mountForm(): Promise<void> {
  if (!paymentMount.value || !buttonMount.value) {
    loading.value = false;
    emit('ready');
    return;
  }
  try {
    const handle = await mountPaymentForm({
      paymentIntent: props.paymentIntent,
      paymentMount: paymentMount.value,
      buttonMount: buttonMount.value,
      submitButtonLabel: props.submitButtonLabel,
      surfaceColor: props.surfaceColor,
      paymentMethods: props.paymentMethods,
      subscription: props.subscription,
      billingAddress: props.billingAddress,
      onButtonClick: () => {
        paymentError.value = '';
        payAttempted = true;
        return (props.onButtonClick ?? (() => Promise.resolve()))();
      },
      onSubmitted: () => {
        submitted.value = true;
      },
      selectDefaultPaymentMethod: props.selectDefaultPaymentMethod,
      onPaymentMethodChange: (type) => {
        paymentError.value = '';
        emit('method-change', type);
      },
      onPaymentError: () => {
        paymentError.value = t('checkout/step/create/payment_error/fallback');
        remountAfterPaymentError();
      },
      onSuccess: async (intentId) => {
        try {
          await props.onSubmit(intentId);
          emit('success');
        } catch (e) {
          captureException(e, {
            extra: {
              context: 'ChargebeePaymentForm.onSubmit',
              paymentIntentId: intentId,
            },
          });
          error.value = t('checkout/step/create/payment_error/fallback');
          emit('error', e);
        }
      },
      onError: (e) => {
        captureException(e, {
          extra: {
            context: 'ChargebeePaymentForm.chargebeeComponentsError',
          },
        });
        error.value = t('checkout/step/create/payment_error/fallback');
        emit('error', e);
      },
      onClose: () => emit('close'),
      // Asked after chargebee.js loads and after each component mounts: a form
      // whose host is gone by then is closed by the composable and comes back
      // null, without tearing down a newer form that has taken over.
      isCancelled: () => isUnmounted,
    });
    if (!handle) return;
    cleanup = handle.unmount;
  } catch (e) {
    captureException(e, {
      extra: { context: 'ChargebeePaymentForm.mountPaymentForm' },
    });
    error.value = t('checkout/step/create/payment_error/fallback');
    emit('error', e);
  } finally {
    loading.value = false;
    emit('ready');
  }
}

onMounted(mountForm);

onBeforeUnmount(() => {
  isUnmounted = true;
  cleanup?.();
});
</script>
